// Bridges p-datepicker models and `DateOnly` search filters, in both directions.
//
// p-datepicker emits a store-timezone ISO moment, but a `DateOnly?` filter on the API needs a
// plain YYYY-MM-DD. The two can't share one property: binding a picker straight to the filter
// would make it re-expand the date back to an ISO and fight the conversion forever. So the host
// keeps a separate picker-bound model per filter and declares the pairing here; this mixin owns
// the translation each way, and — critically — seeds the models from the filters on load so the
// pickers show the dates the grid is actually filtered on.
//
// Hosting component must declare a reactive `filters` object and `datePickerFilters` in `data()`:
//
//     datePickerFilters: [
//         { model: 'dateRange.startDate', filter: 'startDate' },
//         { model: 'dateRange.endDate', filter: 'endDate', isEndDate: true }
//     ]
//
// `model` is a dot path to the picker-bound property, `filter` a key on `filters`. Set
// `isEndDate` to match a picker that carries the `is-end-date` prop, so the seeded value is the
// end-of-day moment that picker would itself emit — otherwise it re-emits a converted value and
// overwrites the filter on load.
//
// List this AFTER SearchFilterStorage in `mixins`: mixin hooks run in listed order, and seeding
// has to see the filters that mixin's created() hook restores from localStorage.
import momentTz from 'moment-timezone';
import { useSelectListsStore } from '@/stores/selectLists.js';

function setByPath(target, path, value) {
    const keys = path.split('.');
    const leaf = keys.pop();
    let node = target;
    for (const key of keys)
        node = node[key];
    node[leaf] = value;
}

export default {
    data() {
        return {
            datePickersSeeded: false
        };
    },
    computed: {
        storeTimeZone() {
            return useSelectListsStore().lists.storeTimeZone ?? '';
        }
    },
    watch: {
        // The store tz can arrive asynchronously after created(); seed once it's known. Not
        // immediate: an immediate watcher fires during state init, before SearchFilterStorage's
        // created() has restored the saved filters, which would seed from empty filters and lock
        // in null dates.
        storeTimeZone(tz) {
            if (tz)
                this.seedDatePickersFromFilters();
        }
    },
    created() {
        for (const pairing of this.datePickerFilters ?? [])
            this.$watch(pairing.model, value => (this.filters[pairing.filter] = this.toDateParam(value)));

        this.seedDatePickersFromFilters();
    },
    methods: {
        // Rebuild the picker ISO models from the (store-local YYYY-MM-DD) filters once the store
        // timezone is known. Runs once. Setting a model re-derives the same YYYY-MM-DD filter value
        // (idempotent) — no feedback loop, because filters.* is not bound to any picker.
        seedDatePickersFromFilters() {
            if (this.datePickersSeeded)
                return;
            if (!this.storeTimeZone)
                return;
            this.datePickersSeeded = true;
            for (const pairing of this.datePickerFilters ?? [])
                setByPath(this, pairing.model, this.toPickerModel(this.filters[pairing.filter], pairing.isEndDate));
        },
        // Store-local YYYY-MM-DD filter value to the ISO moment the matching picker would emit.
        toPickerModel(value, isEndDate) {
            if (!value || !this.storeTimeZone)
                return null;
            const day = momentTz.tz(String(value).substring(0, 10), 'YYYY-MM-DD', this.storeTimeZone);
            return (isEndDate
                ? day.endOf('day').subtract(3, 'milliseconds')
                : day.startOf('day')
            ).toISOString();
        },
        // The pickers emit store-timezone ISO moments. Recover the store-LOCAL calendar date for
        // the DateOnly API filter — a plain split('T')[0] of the UTC ISO lands a day early for
        // positive-UTC-offset stores, which would also drift the seeded value on every remount.
        // Without a zone, momentTz.utc(value).tz('') returns undefined (the getter overload) and
        // .format() would throw, so drop the filter until the zone is known. The picker re-emits a
        // corrected value once it loads, which runs this again and sets the filter properly.
        toDateParam(value) {
            if (!value || !this.storeTimeZone)
                return null;
            return momentTz.utc(value).tz(this.storeTimeZone).format('YYYY-MM-DD');
        }
    }
};
